"use client";

import { useState } from "react";
import { formatTimeDisplay } from "@/lib/utils/time";
import {
  Calendar,
  Users,
  Clock,
  TrendingUp,
  Settings,
  BarChart3,
  Image as ImageIcon,
  Languages,
  Activity,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import DataTable from "@/components/admin/shared/data-table";

interface StudioDetailsProps {
  studio: any;
  bookings?: any[];
  analytics?: {
    utilization: number;
    revenue: number;
    popularSlots: Array<{ time: string; bookings: number }>;
    monthlyTrend: Array<{ month: string; bookings: number; revenue: number }>;
  };
  onEdit?: () => void;
  canEdit?: boolean;
  venueDefaultLanguage?: string;
}

export default function StudioDetails({
  studio,
  bookings = [],
  analytics,
  onEdit,
  canEdit = true,
  venueDefaultLanguage = "en",
}: StudioDetailsProps) {
  const [activeTab, setActiveTab] = useState("overview");
  // Use venue's default language for time formatting (24h for Nordic languages)
  const locale = venueDefaultLanguage;

  const upcomingBookings = bookings
    .filter((b) => new Date(b.date) >= new Date())
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(0, 5);

  const bookingColumns = [
    {
      key: "date" as const,
      label: "Date",
      render: (value: string) => new Date(value).toLocaleDateString(),
    },
    {
      key: "time" as const,
      label: "Time",
      render: (value: string) => value,
    },
    {
      key: "show_name" as const,
      label: "Show",
    },
    {
      key: "participants" as const,
      label: "Participants",
      render: (value: number) => `${value} people`,
    },
    {
      key: "status" as const,
      label: "Status",
      render: (value: string) => (
        <Badge variant={value === "confirmed" ? "default" : "secondary"}>
          {value}
        </Badge>
      ),
    },
  ];

  const equipmentList = Object.entries(studio.equipment || {})
    .filter(([_, value]) => value)
    .map(([key, _]) => key);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{studio.name}</h1>
          <div className="flex items-center gap-4 mt-2">
            <Badge
              variant={studio.status === "active" ? "default" : "secondary"}
            >
              {studio.status}
            </Badge>
            <span className="text-muted-foreground">
              Capacity: {studio.capacity_min}-{studio.capacity_max} people
            </span>
          </div>
        </div>
        {canEdit && (
          <Button onClick={onEdit}>
            <Settings className="h-4 w-4 mr-2" />
            Edit Studio
          </Button>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              Utilization Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics?.utilization || 0}%
            </div>
            <Progress value={analytics?.utilization || 0} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              Monthly Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${analytics?.revenue?.toLocaleString() || "0"}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              <TrendingUp className="h-3 w-3 inline mr-1" />
              +12% from last month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Total Shows</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {studio._count?.shows || 0}
            </div>
            <p className="text-sm text-muted-foreground mt-1">Active shows</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              Total Bookings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {studio._count?.bookings || 0}
            </div>
            <p className="text-sm text-muted-foreground mt-1">All time</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">
            <Activity className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="configuration">
            <Settings className="h-4 w-4 mr-2" />
            Configuration
          </TabsTrigger>
          <TabsTrigger value="analytics">
            <BarChart3 className="h-4 w-4 mr-2" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="media">
            <ImageIcon className="h-4 w-4 mr-2" />
            Media
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Studio Info */}
          <Card>
            <CardHeader>
              <CardTitle>Studio Information</CardTitle>
              <CardDescription>Key details and current status</CardDescription>
            </CardHeader>
            <CardContent>
              {studio.infos?.map((info: any, index: number) => (
                <div key={`${info.lang}-${index}`} className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Languages className="h-4 w-4" />
                    <span className="font-medium">
                      {info.lang.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {info.information}
                  </p>
                </div>
              )) || (
                <p className="text-sm text-muted-foreground">
                  No information available
                </p>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Bookings */}
          <Card>
            <CardHeader>
              <CardTitle>Upcoming Bookings</CardTitle>
              <CardDescription>
                Next 5 scheduled bookings for this studio
              </CardDescription>
            </CardHeader>
            <CardContent>
              {upcomingBookings.length > 0 ? (
                <DataTable data={upcomingBookings} columns={bookingColumns} />
              ) : (
                <p className="text-center py-8 text-muted-foreground">
                  No upcoming bookings scheduled
                </p>
              )}
            </CardContent>
          </Card>

          {/* Availability Calendar */}
          <Card>
            <CardHeader>
              <CardTitle>Availability Calendar</CardTitle>
              <CardDescription>
                Studio availability for the next 30 days
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                <Calendar className="h-8 w-8 mr-2" />
                Calendar component would go here
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="configuration" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Equipment & Features</CardTitle>
              <CardDescription>
                Available equipment and special features
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {equipmentList.map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <div className="h-2 w-2 bg-green-500 rounded-full" />
                    <span className="capitalize">
                      {item.replace(/_/g, " ")}
                    </span>
                  </div>
                ))}
              </div>
              {equipmentList.length === 0 && (
                <p className="text-muted-foreground">No equipment configured</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Special Requirements</CardTitle>
              <CardDescription>
                Studio-specific rules and requirements
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                No special requirements configured
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Maintenance Schedule</CardTitle>
              <CardDescription>Regular maintenance windows</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                No maintenance schedule configured
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Popular Time Slots</CardTitle>
                <CardDescription>
                  Most booked times for this studio
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {analytics?.popularSlots?.map((slot, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between"
                    >
                      <span className="text-sm">{formatTimeDisplay(slot.time, locale)}</span>
                      <div className="flex items-center gap-2">
                        <Progress
                          value={(slot.bookings / 30) * 100}
                          className="w-24"
                        />
                        <span className="text-sm text-muted-foreground w-12 text-right">
                          {slot.bookings}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Monthly Trend</CardTitle>
                <CardDescription>
                  Bookings and revenue over time
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  <BarChart3 className="h-8 w-8 mr-2" />
                  Chart component would go here
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Performance Metrics</CardTitle>
              <CardDescription>
                Detailed studio performance analysis
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Avg. Booking Size
                  </p>
                  <p className="text-xl font-semibold">8.5 people</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Peak Hour</p>
                  <p className="text-xl font-semibold">7:00 PM</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Avg. Revenue/Booking
                  </p>
                  <p className="text-xl font-semibold">$125</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Repeat Rate</p>
                  <p className="text-xl font-semibold">65%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="media" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Studio Photos & Videos</CardTitle>
              <CardDescription>Media gallery for this studio</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {studio.studio_medias?.map((media: any) => (
                  <div
                    key={media.id}
                    className="relative aspect-square bg-muted rounded-lg overflow-hidden"
                  >
                    {media.type === "image" ? (
                      <img
                        src={media.url}
                        alt="Studio"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <video
                        src={media.url}
                        className="w-full h-full object-cover"
                        controls
                      />
                    )}
                    {media.is_primary && (
                      <Badge
                        className="absolute top-2 left-2"
                        variant="secondary"
                      >
                        Primary
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
              {(!studio.studio_medias || studio.studio_medias.length === 0) && (
                <div className="text-center py-12 text-muted-foreground">
                  <ImageIcon className="h-12 w-12 mx-auto mb-4" />
                  <p>No media uploaded yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
